/**
 * Frontend feature flags (build-time, Vite env)
 * =============================================================================
 * Label Doctor has no runtime flag-delivery endpoint — the frontend learns
 * backend feature state from build-time Vite envs (`import.meta.env.VITE_*`),
 * the same mechanism already used for `VITE_API_URL` / `VITE_FIREBASE_*`.
 *
 * These flags are UX only: they hide dead entry points so users don't see a
 * control the server would reject. The security boundary lives server-side
 * (see the matching backend flags), NOT here.
 *
 * CUSTOM_LABEL_UPLOAD — the dormant custom-label upload feature
 * (`POST /api/v2/keyword-search/upload-label` + `region='CUSTOM'` search
 * rows). Gated OFF by default to mirror the backend `CUSTOM_LABEL_UPLOAD_
 * ENABLED` flag (tenant-isolation sub-project D — closes a cross-tenant
 * leak; 0 prod usage). Set `VITE_CUSTOM_LABEL_UPLOAD_ENABLED=true` at build
 * time to re-enable the UI once real per-org isolation exists.
 *
 * CI_CORPUS — the dormant competitive-intel (product pages + press
 * releases) corpus, admin-gated on Cogni/Investigator. Gated OFF by default
 * to mirror the backend `CI_CORPUS_ENABLED` flag (`settings.CI_CORPUS_
 * ENABLED`, default False — see `ci_routes.py` / `research_routes.py`).
 * Set `VITE_CI_CORPUS_ENABLED=true` at build time to expose the 'ci' mode
 * pill once the corpus is ready for a given environment.
 *
 * (The CEPI Agents demo replay is a RUNTIME `?demo=1` switch, not a build flag:
 * see `demo/cepiDemoReplay.ts`.)
 */

/**
 * Pure truthiness parser for a build-time env flag. Any of `true` / `1` /
 * `yes` (case-insensitive, whitespace-trimmed) enables; everything else —
 * including `undefined` (env not set) — is OFF. Kept pure + exported so the
 * gating logic is unit-testable without mocking `import.meta.env`.
 */
export function parseFlag(raw: string | boolean | undefined | null): boolean {
  if (typeof raw === 'boolean') return raw;
  if (raw == null) return false;
  return ['true', '1', 'yes'].includes(String(raw).trim().toLowerCase());
}

/** Whether the dormant custom-label upload UI should be shown. Default OFF. */
export function isCustomLabelUploadEnabled(): boolean {
  return parseFlag(import.meta.env['VITE_CUSTOM_LABEL_UPLOAD_ENABLED']);
}

/** Whether the dormant competitive-intel ('ci') mode should be shown. Default OFF. */
export function isCompetitiveIntelEnabled(): boolean {
  return parseFlag(import.meta.env['VITE_CI_CORPUS_ENABLED']);
}

/**
 * Whether the bbox citation highlight is drawn in the PDF viewers.  Default
 * OFF — this is a deliberate kill switch, not a dormant feature.
 *
 * Why it is off: a chunk's stored bbox describes its ``core_text``, but
 * retrieval matches ``chunk_text`` — core PLUS ~15% of the neighbouring
 * chunks stapled on either side by ``overlap_assembler`` ("core_text never
 * contains overlap; only chunk_text does").  When the match lands in that
 * overlap the highlight points at the core instead, which is a different
 * place on the page — and, measured on prod, a different PAGE in 44.9% of
 * cases.  Confirmed live on 2026-09-01: a Jatenzo CEPI answer quoted text
 * from page 11 and the viewer highlighted page 12 starting mid-sentence.
 *
 * Scrolling to the cited page is UNAFFECTED — only the rectangle is
 * suppressed.  A box drawn on the wrong text asserts something false; no box
 * simply asserts less.
 *
 * Set ``VITE_CITATION_BBOX_HIGHLIGHT_ENABLED=true`` at build time to restore
 * the previous behaviour, and delete this flag once citation identity is
 * resolved (see docs/plans/2026-09-01-citation-highlight-root-cause.md).
 */
export function isCitationBboxHighlightEnabled(): boolean {
  return parseFlag(import.meta.env['VITE_CITATION_BBOX_HIGHLIGHT_ENABLED']);
}

/**
 * Whether the tag/name/manufacturer suggestions (All-scope popover, kind scopes) are shown.
 * Default ON — this is a temporary kill switch, and it is the ONE flag here
 * whose default is "shown", because it gates a feature that is currently LIVE
 * (unlike the dormant/kill-switch flags above, which default OFF).
 *
 * Why the switch exists: the dropdown is served from the `mv_search_suggestions`
 * materialized view, which on prod is currently UNPOPULATED (created `WITH NO
 * DATA`, never `REFRESH`ed), and `drugs.tags` — formerly the source of the 'tag' tier,
 * which is no longer a suggestion kind — is only ~5.6% backfilled (all US; UK/CA/AU/JP/CN/EU at 0%). An unpopulated MV
 * makes `GET /search/suggest` error, and the only way to use the bar is to click
 * a dropdown row (there is no Enter-to-add), so on prod it is a dead/broken box.
 * Hiding it is cleaner than shipping a control that cannot work yet.
 *
 * Because it is `_DISABLED` (not `_ENABLED`), the default — env unset — leaves
 * the bar SHOWN, so dev/staging/tests are unaffected. Set
 * `VITE_SEARCH_SUGGEST_DISABLED=true` at build time (prod) to hide it. Remove
 * that env (or run the tag backfill + `REFRESH MATERIALIZED VIEW
 * mv_search_suggestions`, then rebuild) to restore it. Delete this flag once
 * the backfill is complete.
 */
export function isSearchSuggestBarEnabled(): boolean {
  return !parseFlag(import.meta.env['VITE_SEARCH_SUGGEST_DISABLED']);
}
